import type { EntityRow } from "../Dashboard/types";

/**
 * Rows a grouped page asks for per group. Uncapped, a page returned every row
 * of its groups, which on a large record was the whole scope (10k+ rows in one
 * response); "Show more" raises one group's allowance. At the grid's 50 groups
 * a page, 20 bounds a grouped page at the 1,000 rows an ungrouped page may
 * hold (`SEARCH_PAGE_SIZE_MAX` on the server). Spec:
 * .claude/features/rms-grouped-page-row-cap.md.
 */
export const GROUP_ROW_LIMIT = 20;

/**
 * The page's per-group allowances after "Show more" on group `rank`: that
 * group gets GROUP_ROW_LIMIT more rows than it has now. The allowances are
 * part of the page's request, so a refetch of the page returns the same rows.
 */
export function withMoreRowsFor(
  limits: Record<number, number> | undefined,
  rank: number
): Record<number, number> {
  const current = limits?.[rank] ?? GROUP_ROW_LIMIT;
  return { ...limits, [rank]: current + GROUP_ROW_LIMIT };
}

/** A group the page holds only part of. */
export interface GroupShortfall {
  rank: number;
  /** Rows of the group the page holds. */
  shown: number;
  /** The group's full row count. */
  size: number;
  /** Index into the page's rows of the group's last row. */
  lastIndex: number;
}

/**
 * The groups the page holds only part of, in page order. A row the server
 * did not send for this page (retained after an edit, #11363, or a
 * processing placeholder) neither counts as shown nor carries a size; a group
 * made only of such rows has no server rank (it is negative or absent) and is
 * never short.
 */
export function groupShortfalls(data: EntityRow[]): GroupShortfall[] {
  const byRank = new Map<number, GroupShortfall>();
  data.forEach((row, index) => {
    const rank = row.group_rank;
    if (rank == null || rank < 1) return;
    const entry = byRank.get(rank) ?? {
      rank,
      shown: 0,
      size: 0,
      lastIndex: index,
    };
    entry.lastIndex = index;
    if (!row._retained && !row._processing) {
      entry.shown += 1;
      entry.size = Math.max(entry.size, row.group_size ?? 0);
    }
    byRank.set(rank, entry);
  });
  return [...byRank.values()].filter((g) => g.size > g.shown);
}
